// @flow
import { createAction, disbatch } from 'redux-act';

import { selectPricingInfos } from '@gandi/billing';
import {
  getCart,
  addCartProduct,
  removeProduct,
  selectOneProduct,
} from '@gandi/cart';
import type {
  ProductData,
  ProductType,
  Process,
  ProductPayload,
} from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';
import { selectFromConfig } from '@gandi/redux-config';
import { createResource } from '@gandi/resource/redux';

import type { Resource, FetchResponse } from '@gandi/resource/redux';
import { localeSelector as selectLocale } from '@gandi/translate';

import { createLogger } from 'gandi.redux-logger';

import { startSSE, closeEventSource } from 'src/modules/app/sse';

import type { GetState, Dispatch } from '../../types';
import type { ResultItem as DomainTransferItem } from '../types';

import { fetchAvailability } from './fallbacks';
import { selectSearchValue } from './selectors';

const logger = createLogger('transferIn');

const selectTransferUrl = selectFromConfig<string>('api.transferUrl');

export const saveSearch: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Save search');
export const resetAllResults: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Reset all results');
export const resetSearch: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Reset search');
export const toggleSpinner: {
  (...Array<boolean>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Toggle spinner');

export const setWorkingFlag: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Set the working flag for a domain');
export const unsetWorkingFlag: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Unset the working flag for a domain');

export const startAddingToCart: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Start adding a domain to the cart');
export const stopAddingToCart: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Stop adding a domain to the cart');

export function getResultProductData(domain: DomainTransferItem): {
  extra: { identifier?: string, label?: string, optimisticId?: string },
  productData: {
    authentication?: { password: string },
    bundle_id?: number,
    fqdn?: string,
    identifier?: string,
    label?: string,
    metadatas?: ProductData['metadatas'],
    payload?: ProductPayload,
    process: Process,
    product_type: ProductType,
    service_id?: string,
  },
} {
  const productData = {
    product_type: 'domain',
    process: 'transfer',
    payload: {
      fqdn: domain.fqdn,
      duration: domain.durations[0],
      change_owner: domain.extTrade,
      authinfoForTransfer: domain.authinfoForTransfer,
      tld: domain.tld,
      options: {
        durations: domain.durations,
      },
      mailbox_optin: true,
      corporate: domain.corporate,
      internal: domain.internal,
    },
    metadatas: {
      corporate: domain.corporate,
    },
  };

  const extra = {
    label: domain.fqdn,
  };

  // $FlowFixMe[incompatible-return]
  return { productData, extra };
}

export const addToCart =
  (domain: DomainTransferItem): ((dispatch: Dispatch) => any) =>
  dispatch => {
    dispatch(startAddingToCart(domain.fqdn));

    const { productData, extra } = getResultProductData(domain);

    // Add the product via the API, bypass dry-run.
    return dispatch(
      addCartProduct({
        productData,
        extra,
        options: { headers: { 'X-Dry-Run': 0 } },
      }),
    )
      .then(() => dispatch(getCart()))
      .then(() => dispatch(stopAddingToCart(domain.fqdn)));
  };

export const startRemovingFromCart: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Start removing a domain from the cart');
export const stopRemovingFromCart: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Stop removing a domain from the cart');

/**
 * Proxy the product removal to the Cart.
 * Optional: Remove the product optimistically while waiting for the API
 */
export const removeFromCart =
  (
    domain: DomainTransferItem,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<mixed>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const product = selectOneProduct(state, { fqdn: domain.fqdn });

    dispatch(startRemovingFromCart(domain.fqdn));

    if (product && product.id) {
      dispatch(logger.info(`Removing ${domain.fqdn} (${product.id}) via API.`));
      return dispatch(removeProduct(product.id)).then(() =>
        dispatch(stopRemovingFromCart(domain.fqdn)),
      );
    }

    if (product && !product.id) {
      dispatch(stopRemovingFromCart(domain.fqdn));
      return Promise.reject(
        new Error(
          `Product ${domain.fqdn} does not have an ID, refusing to remove.`,
        ),
      );
    }

    return Promise.reject(
      new Error(`Could not find product ${domain.fqdn} in Cart.`),
    );
  };

// TLD INFO -----------------------------------------------------------
export const tldInfo: Resource<> = createResource('tldInfo');

export type TldInfoApiResponse = {
  category: string,
  lock: boolean,
  authinfo_for_transfer: boolean,
  name: string,
  ext_trade: boolean,
  ...
};

export type FetchTldInfoResponseSuccess = FetchResponse<
  200,
  Array<TldInfoApiResponse>,
>;
export const fetchTldInfo =
  (fqdn: Fqdn): (Dispatch => Promise<FetchTldInfoResponseSuccess>) =>
  (dispatch): Promise<FetchTldInfoResponseSuccess> =>
    dispatch(
      tldInfo.fetchOne(`/domain-anonymous/tlds/${fqdn}`, {
        label: `tldInfo_${fqdn}`,
        fqdn,
        on200: (response: FetchResponse<200, any>) => response,
        not200: (response: FetchResponse<any, any>) => response,
      }),
    ).catch(err => {
      dispatch(logger.error({ err }, 'Cannot fetch tldInfo'));
    });

// TRANSFER IN -----------------------------------------------------------------
export const clearTransferInAvailability: {
  (...mixed): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Clear transferIn availability');
export const requestTransferInAvailability: {
  (...Array<Fqdn>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Request transferIn availability');
export const receiveTransferInAvailability: {
  ({ data: any, fqdn: Fqdn }): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Receive transferIn availability');
export const receiveTransferInPrices: {
  ({ data: any, fqdn: Fqdn }): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Receive transferIn prices');
export const setFallbackAvailability: {
  (...Array<{ availability: any, fqdn: string }>): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[TransferIn] Set fallback availability');

type SSEEvent = {
  event:
    | 'transferin'
    | 'transferin_failed'
    | 'billing'
    | 'billing_failed'
    | 'done'
    | 'error',
  data: string,
  ...
};
export type MakeSSEListeners = () => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  [event: string]: void | (SSEEvent => mixed),
  ...
};
const makeSSEListeners: MakeSSEListeners = () => (dispatch: Dispatch) => {
  function transferin(event: SSEEvent) {
    let result;
    try {
      result = JSON.parse(event.data);
      dispatch(
        receiveTransferInAvailability({ data: result, fqdn: result.fqdn }),
      );

      if (result.available) {
        dispatch(fetchTldInfo(result.fqdn));
      }

      if (!result.available) {
        dispatch(fetchAvailability(result.fqdn)).then(lookup => {
          if (lookup && lookup.data && lookup.data.availability) {
            dispatch(
              setFallbackAvailability({
                availability: lookup.data.availability,
                fqdn: result.fqdn,
              }),
            );
          }
        });
      }
      dispatch(unsetWorkingFlag(result.fqdn));
    } catch (err) {
      logger.error(err, 'error parse response in transferin');
      error(event);
    }
  }

  function done(/* event: SSEEvent */) {
    dispatch(logger.info(`done`));
    closeEventSource();
  }

  function transferin_failed(event: SSEEvent) {
    dispatch(logger.error(event, `transferin_failed`));
    let result;
    try {
      result = JSON.parse(event.data);
      dispatch(
        receiveTransferInAvailability({
          data: { invalid: true },
          fqdn: result.fqdn,
        }),
      );
      dispatch(unsetWorkingFlag(result.fqdn));
    } catch (err) {
      logger.error(err, 'error parse response in transferin_failed');
    }
  }

  function billing(event: SSEEvent) {
    let result;
    try {
      result = JSON.parse(event.data);
      dispatch(
        receiveTransferInPrices({
          data: { result },
          fqdn: result.fqdn,
        }),
      );
      dispatch(unsetWorkingFlag(result.fqdn));
    } catch (err) {
      logger.error(err, 'error parse response in billing');
      error(event);
    }
  }

  function error(event: SSEEvent) {
    dispatch(logger.error(event, `error`));
  }

  function billing_failed(event: SSEEvent) {
    let result;
    try {
      result = JSON.parse(event.data);
      dispatch(logger.error(event, `billing_failed`));
      dispatch(unsetWorkingFlag(result.fqdn));
    } catch (err) {
      logger.error(err, 'error parse response in billing_failed');
      error(event);
    }
  }

  return {
    transferin,
    transferin_failed,
    done,
    error,
    billing,
    billing_failed,
  };
};

const getTransferinUrl =
  (
    searchInputs: Array<string>,
  ): ((dispatch: Dispatch, getState: GetState) => string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const url = selectTransferUrl(getState());

    const transferInParams = new URLSearchParams({
      fqdn: searchInputs.join(','),
    });

    const pricingInfos = selectPricingInfos(getState());
    const locale = selectLocale(getState());

    for (const [key, value] of Object.entries({ ...pricingInfos, locale })) {
      if (value && value !== '') {
        if (key === 'locale') {
          // $FlowFixMe[incompatible-call]
          transferInParams.append('lang', value);
        } else {
          // $FlowFixMe[incompatible-call]
          transferInParams.append(key, value);
        }
      }
    }

    return `${url}?${transferInParams.toString()}`;
  };

/**
 * Load the results for user's current search input using Domain API.
 */
export const loadResults =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const searchValue = selectSearchValue(getState());
    const searchInputs = searchValue.split(' ').filter(Boolean);

    searchInputs.map(input => dispatch(setWorkingFlag(input)));
    const transferInUrl = dispatch(getTransferinUrl(searchInputs));
    // $FlowFixMe[incompatible-call] - flow doesnt like enums
    return dispatch(startSSE(transferInUrl, makeSSEListeners));
  };

export const validateSearch =
  (inputValue: string): (Dispatch => Promise<void>) =>
  async dispatch => {
    disbatch(
      dispatch,
      saveSearch(inputValue),
      resetAllResults(),
      inputValue ? toggleSpinner(true) : toggleSpinner(false),
    );

    if (inputValue) {
      await dispatch(loadResults());
      dispatch(toggleSpinner(false));
    }
  };
